import React, { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import {
  ArrowLeft, SearchX, RefreshCw, Settings, Share2, Lock, Copy,
  Gem, Gamepad2, TrendingUp, Users, Package, ShoppingBag
} from 'lucide-react'
import { motion } from 'motion/react'
import toast from 'react-hot-toast'
import { useProfileStore } from '../store/useProfileStore'
import useAppStore from '../../../store/useAppStore'
import ProfileHeader from '../components/ProfileHeader'
import ProfileSkinsGrid from '../components/ProfileSkinsGrid'
import SkinShowcaseModal from '../../../components/SkinShowcaseModal'
import AnimatedNumber from '../../../components/AnimatedNumber'
import AchievementModal from '../components/AchievementModal'
import {
  accentColor,
  cardPrimaryColor,
  contentBgColor,
  lightenHex,
  profileBackground,
  tileBgColor,
  tilePrimaryColor
} from '../utils/profileColors'
import { getGameLabel } from '../utils/profileFormat'
import { formatPrice } from '../../../utils/formatters'
import useAppEnvironment from '../../../hooks/useAppEnvironment'
import useAudioManager from '../../../hooks/useAudioManager'
import { hapticImpact, hapticNotify } from '../../../utils/haptic'
import { LOGO_SRC } from '../../../config/brand'
import { sendYandexGoal } from '../../../services/yandexMetrikaService'
import { normalizeLanguageCode } from '../../../i18n/config'

const CARD_CLASS = 'rounded-2xl border border-white/10 bg-surface shadow-[inset_0_1px_0_rgba(255,255,255,0.07),0_10px_15px_-3px_rgba(0,0,0,0.25),0_4px_6px_-4px_rgba(0,0,0,0.25)]'
const TILE_CLASS = 'rounded-xl border border-white/5 p-4 shadow-[inset_0_1px_0_rgba(255,255,255,0.05)]'

// Stagger-появление секций (docs/design-system.md, раздел 5)
const SECTIONS_VARIANT = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.08 } }
}
const SECTION_VARIANT = {
  hidden: { opacity: 0, y: 24 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: 'easeOut' } }
}

const SectionTitle = ({ icon: Icon, color, textColor, children }) => (
  <h2 className="text-sm font-bold not-italic uppercase tracking-wide mb-3 flex items-center gap-2" style={{ color: textColor }}>
    {Icon && (
      <span
        className={`w-6 h-6 rounded-lg flex items-center justify-center shrink-0 ${color ? '' : 'bg-accent-primary/10'}`}
        style={color ? { backgroundColor: `${color}1A` } : undefined}
      >
        <Icon size={13} style={color ? { color } : undefined} className={color ? '' : 'text-accent-primary'} />
      </span>
    )}
    {children}
  </h2>
)

const ProfileSkeleton = () => (
  <div className="mx-auto w-full lg:max-w-6xl lg:px-6 lg:py-8 p-4 lg:grid lg:grid-cols-12 lg:items-start lg:gap-8">
    <div className="lg:col-span-4 lg:sticky lg:top-24">
      <div className={`${CARD_CLASS} p-6 flex flex-col items-center`}>
        <div className="w-24 h-24 rounded-full bg-gray-700 animate-pulse mb-3" />
        <div className="h-6 w-32 bg-gray-700 rounded animate-pulse mb-4" />
        <div className="h-4 w-40 bg-gray-800 rounded animate-pulse" />
      </div>
    </div>
    <div className="lg:col-span-8 mt-6 lg:mt-0 space-y-6">
      {[0, 1, 2].map((i) => (
        <div key={i} className={`${CARD_CLASS} p-5`}>
          <div className="h-5 w-40 bg-gray-700 rounded animate-pulse mb-4" />
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
            {[0, 1, 2].map((j) => (
              <div key={j} className="aspect-[3/4] bg-gray-800 rounded-2xl animate-pulse" />
            ))}
          </div>
        </div>
      ))}
    </div>
  </div>
)

/**
 * Экран публичного профиля.
 * `/profile` (без slug) — свой профиль, корневой таб нижнего меню.
 * `/profile/:slug` — свой по ссылке или чужой (диплинк profile_<slug>).
 * Слева в шапке — кнопка «назад» (история роутера, фолбэк — главная).
 * Набор ключей ответа всегда одинаковый: недоступные по уровню поля приходят null.
 * Блоки рисуем и по visibility, и по null в конкретном поле.
 */
const PublicProfile = () => {
  const { slug } = useParams()
  const navigate = useNavigate()
  const { t, i18n } = useTranslation()
  const { isTelegramMiniApp, isWebBrowser } = useAppEnvironment()
  const isGuest = useAppStore((state) => state.isGuest)
  const openLogin = useAppStore((state) => state.openLogin)
  const { playSound } = useAudioManager({ select: 'select' })
  const {
    profile, isLoading, error, isLikePending,
    loadProfile, setLike, shareProfile, reset
  } = useProfileStore()
  // Выбранный топ-скин для фуллскрин-презентации (виральная витрина под скриншот)
  const [showcase, setShowcase] = useState(null)
  const [selectedAchievement, setSelectedAchievement] = useState(null)
  const language = normalizeLanguageCode(i18n.resolvedLanguage || i18n.language) || 'en'

  useEffect(() => {
    loadProfile(slug)
    return () => reset()
    // loadProfile/reset стабильны (zustand), перезагрузка при смене slug или языка
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug, language, isGuest])

  // Назад по истории роутера; если истории нет (вход по диплинку) — на главную
  const handleBack = () => {
    playSound('select')
    hapticImpact('light')
    if (window.history.length > 1) {
      navigate(-1)
    } else {
      navigate('/')
    }
  }

  const handleRetry = () => loadProfile(slug)

  // Лайк: желаемое состояние 1/0, счётчик — из ответа
  const handleLike = async () => {
    if (!profile || profile.is_owner) return
    if (isGuest) {
      openLogin()
      return
    }
    const want = profile.liked_by_me ? 0 : 1

    // Снять лайк можно всегда; поставить — только при can_like
    if (want === 1 && !profile.can_like) {
      toast(t('publicProfile.likeHint', 'Лайкать могут игроки, которые пополняли баланс или выводили предметы'), { duration: 5000 })
      return
    }

    const res = await setLike(profile.id, want)
    if (res?.ok) {
      hapticNotify('success')
      sendYandexGoal('profile-like')
    } else if (res?.status === 429) {
      toast.error(t('api.rateLimited'))
    } else if (res?.error === 'like_not_allowed' || res?.status === 403) {
      toast(t('publicProfile.likeHint', 'Лайкать могут игроки, которые пополняли баланс или выводили предметы'), { duration: 5000 })
    } else if (res?.error === 'own_profile' || res?.error === 'not_found') {
      // редкие случаи — молча
    } else if (res) {
      toast.error(t('publicProfile.likeError', 'Не удалось изменить лайк'))
    }
  }

  // «Поделиться»: только владелец и только в Telegram (веб без Telegram — ручка вернёт 400)
  const canShare = profile?.is_owner === true && isTelegramMiniApp &&
    typeof window.Telegram?.WebApp?.shareMessage === 'function'

  const handleShare = async () => {
    hapticImpact('medium')
    const res = await shareProfile()
    const messageId = res?.data?.prepared_message?.id

    if (!messageId) {
      toast.error(t('publicProfile.shareError', 'Не получилось, попробуй ещё раз'))
      return
    }

    sendYandexGoal('profile-share')
    // prepared_message живёт ограниченное время — id не кешируем
    window.Telegram.WebApp.shareMessage(messageId, () => {})
  }

  // Копирование ссылки на профиль (свой или чужой):
  // в Telegram — диплинк текущего бота, в браузере — URL сайта
  const handleCopyLink = async () => {
    if (!profile?.id) return
    hapticImpact('light')
    const bot = import.meta.env.VITE_TELEGRAM_BOT || 'skinozbot'
    const link = isWebBrowser
      ? `${window.location.origin}/profile/${profile.id}`
      : `https://t.me/${bot}/app?startapp=profile_${profile.id}`
    try {
      await navigator.clipboard.writeText(link)
      toast.success(t('referrals.linkCopied', 'Ссылка скопирована!'))
      sendYandexGoal('profile-copy-link')
    } catch (e) {
      toast.error(t('publicProfile.shareError', 'Не получилось, попробуй ещё раз'))
    }
  }

  if (isLoading) {
    return <ProfileSkeleton />
  }

  if (error === 'not_found') {
    return (
      <div className="mx-auto w-full max-w-md p-4 py-16 flex flex-col items-center text-center">
        <div className="w-20 h-20 rounded-full bg-surface flex items-center justify-center mb-4 border border-white/10">
          <SearchX size={36} className="text-text-secondary" />
        </div>
        <h1 className="text-xl font-bold text-white mb-2">
          {t('publicProfile.notFound', 'Профиль не найден')}
        </h1>
        <p className="text-text-secondary text-sm mb-6">
          {t('publicProfile.notFoundDescription', 'Возможно, ссылка устарела или профиль был удалён')}
        </p>
        <button
          onClick={handleBack}
          className="px-6 py-3 rounded-xl border border-accent-primary/30 bg-accent-primary/10 text-accent-primary font-medium transition-all active:scale-[0.98]"
        >
          {t('common.back', 'Назад')}
        </button>
      </div>
    )
  }

  if (error) {
    return (
      <div className="mx-auto w-full max-w-md p-4 py-16 flex flex-col items-center text-center">
        <div className="w-20 h-20 rounded-full bg-surface flex items-center justify-center mb-4 border border-white/10">
          <RefreshCw size={36} className="text-text-secondary" />
        </div>
        <h1 className="text-xl font-bold text-white mb-2">
          {error === 'rate_limited' ? t('api.rateLimited') : t('publicProfile.loadError', 'Не удалось загрузить профиль')}
        </h1>
        <button
          onClick={handleRetry}
          className="mt-4 px-6 py-3 rounded-xl bg-gradient-to-r from-accent-primary to-accent-secondary text-dark-primary font-bold transition-all active:scale-[0.98]"
        >
          {t('publicProfile.retry', 'Повторить')}
        </button>
      </div>
    )
  }

  if (!profile) {
    return <ProfileSkeleton />
  }

  const colors = profile.colors
  const isOwner = profile.is_owner === true
  const visibility = profile.visibility

  // Клик по топ-скину → фуллскрин-витрина (звук + вибрация, как у карточек)
  const openShowcase = (item, rank) => {
    playSound('select')
    hapticImpact('light')
    setShowcase({ item, rank })
  }

  // Предпросмотр для владельца собираем сами: владелец видит всё,
  // скрываем блоки по таблице уровней для текущего visibility
  const showPartial = isOwner || visibility === 'partial' || visibility === 'public'
  const showPublic = isOwner || visibility === 'public'

  // Карточки контента — content_bg с автоматическим основным текстом;
  // пользовательский text_secondary применяется только внутри ProfileHeader
  const primary = cardPrimaryColor(colors)
  const contentBg = contentBgColor(colors)
  const tileBg = tileBgColor(colors)
  const tilePrimary = tilePrimaryColor(colors)
  const accent = accentColor(colors)
  const subtleSurface = `${primary}0D`
  const subtleBorder = `${primary}1F`
  const tileSurface = `${tilePrimary}0D`
  const tileBorder = `${tilePrimary}1F`

  const handleAchievementClick = (achievement) => {
    playSound('select')
    hapticImpact('light')
    setSelectedAchievement(achievement)
  }

  const handleOpenAchievements = () => {
    playSound('select')
    hapticImpact('light')
    navigate(isOwner ? '/profile/achievements' : `/profile/${slug || profile.id}/achievements`)
  }

  return (
    <div className="lg:px-6 lg:pb-8">
      <div
        style={{ background: profileBackground(colors) }}
        className="min-h-[80vh] lg:min-h-0 lg:rounded-3xl lg:shadow-[inset_0_1px_0_rgba(255,255,255,0.07),0_10px_15px_-3px_rgba(0,0,0,0.25),0_4px_6px_-4px_rgba(0,0,0,0.25)]"
      >
        <motion.div
          variants={SECTIONS_VARIANT}
          initial="hidden"
          animate="visible"
          className="mx-auto w-full lg:max-w-6xl lg:px-6 lg:py-8 p-4"
        >
        {/* Шапка экрана: слева «назад», справа действия */}
        <motion.div variants={SECTION_VARIANT} className="flex items-center justify-between mb-4">
          <button
            onClick={handleBack}
            className="p-2 rounded-xl bg-surface border border-white/10 shadow-[inset_0_1px_0_rgba(255,255,255,0.05)] hover:bg-opacity-80 transition-all active:scale-[0.98]"
            aria-label={t('common.back', 'Назад')}
          >
            <ArrowLeft size={20} className="text-white" />
          </button>
          <div className="flex items-center gap-2">
            {/* «Поделиться» — иконка в шапке (только владелец и только в Telegram Mini App) */}
            {canShare && (
              <button
                onClick={handleShare}
                className="p-2 rounded-xl bg-surface border border-accent-primary/30 shadow-[inset_0_1px_0_rgba(255,255,255,0.05)] hover:bg-opacity-80 transition-all active:scale-[0.98]"
                aria-label={t('publicProfile.share', 'Поделиться')}
              >
                <Share2 size={20} className="text-accent-primary" />
              </button>
            )}
            {/* Копирование ссылки на профиль — на любом профиле, чтобы можно было поделиться */}
            <button
              onClick={handleCopyLink}
              className="p-2 rounded-xl bg-surface border border-white/10 shadow-[inset_0_1px_0_rgba(255,255,255,0.05)] hover:bg-opacity-80 transition-all active:scale-[0.98]"
              aria-label={t('publicProfile.copyLink', 'Скопировать ссылку')}
            >
              <Copy size={20} className="text-white" />
            </button>
            {/* Настройки (/settings) — только на своём профиле: страница настроек владельца */}
            {isOwner && (
              <button
                onClick={() => {
                  playSound('select')
                  navigate('/settings')
                }}
                className="p-2 rounded-xl bg-surface border border-white/10 shadow-[inset_0_1px_0_rgba(255,255,255,0.05)] hover:bg-opacity-80 transition-all active:scale-[0.98]"
                aria-label={t('publicProfile.appSettings', 'Настройки')}
              >
                <Settings size={20} className="text-white" />
              </button>
            )}
          </div>
        </motion.div>

        <div className="lg:grid lg:grid-cols-12 lg:items-start lg:gap-8">
          {/* Шапка профиля */}
          <motion.div variants={SECTION_VARIANT} className="lg:col-span-4 lg:sticky lg:top-24">
            <ProfileHeader
              profile={profile}
              isOwner={isOwner}
              onLike={handleLike}
              isLikePending={isLikePending}
              onAchievementClick={handleAchievementClick}
              onOpenAchievements={handleOpenAchievements}
            />
          </motion.div>

          {/* Контент */}
          <div className="lg:col-span-8 mt-6 lg:mt-0 space-y-6">
            {/* Скрытый профиль (не владелец) — мини-карточка уже показана выше */}
            {!isOwner && visibility === 'hidden' && (
              <motion.div variants={SECTION_VARIANT} className={`${CARD_CLASS} p-8 flex flex-col items-center text-center`} style={{ backgroundColor: contentBg, borderColor: subtleBorder }}>
                <div className="mb-3 flex h-16 w-16 items-center justify-center rounded-full border"
                  style={{ backgroundColor: subtleSurface, borderColor: subtleBorder }}>
                  <Lock size={28} style={{ color: primary }} />
                </div>
                <h3 className="mb-1 font-bold" style={{ color: primary }}>
                  {t('publicProfile.hidden', 'Профиль скрыт')}
                </h3>
                <p className="text-sm" style={{ color: primary }}>
                  {t('publicProfile.hiddenDescription', 'Владелец скрыл статистику профиля')}
                </p>
              </motion.div>
            )}

            {(showPartial || showPublic) && (
              <>
                {/* Баланс (public) — крупная цифра-акцент по рецепту дизайн-системы,
                    в цвете акцента профиля; первым блоком сразу под шапкой */}
                {showPublic && profile.balance != null && (
                  <motion.div variants={SECTION_VARIANT} className={`${CARD_CLASS} relative overflow-hidden p-5`} style={{ backgroundColor: contentBg, borderColor: subtleBorder }}>
                    <div
                      className="pointer-events-none absolute -right-10 -top-16 h-40 w-40 rounded-full blur-2xl"
                      style={{ backgroundColor: `${accent}1A` }}
                    />
                    <div className="relative">
                      <div className="flex items-center gap-2 mb-2">
                        <Gem size={16} style={{ color: accent }} />
                        <span className="text-xs uppercase tracking-wide" style={{ color: primary }}>
                          {t('publicProfile.balance', 'Баланс')}
                        </span>
                      </div>
                      <div
                        className="bg-clip-text text-transparent text-2xl lg:text-3xl font-bold tabular-nums break-all"
                        style={{ backgroundImage: `linear-gradient(90deg, ${accent}, ${lightenHex(accent)})` }}
                      >
                        <AnimatedNumber value={profile.balance} duration={1} />
                      </div>
                    </div>
                  </motion.div>
                )}

                {/* Любимые режимы (partial+) — стек-шкала долей + легенда, без счётчиков игр */}
                {showPartial && profile.top_games && profile.top_games.length > 0 && (
                  <motion.div variants={SECTION_VARIANT} className={`${CARD_CLASS} p-5`} style={{ backgroundColor: contentBg, borderColor: subtleBorder }}>
                    <SectionTitle icon={Gamepad2} color={accent} textColor={primary}>{t('publicProfile.topGames', 'Любимые режимы')}</SectionTitle>
                    {(() => {
                      const total = profile.top_games.reduce((s, g) => s + g.count, 0) || 1
                      const stops = [
                        `linear-gradient(90deg, ${accent}, ${accent}CC)`,
                        `linear-gradient(90deg, ${accent}99, ${accent}66)`,
                        `linear-gradient(90deg, ${accent}4D, ${accent}33)`
                      ]
                      return (
                        <>
                          <div
                            className="flex h-3 overflow-hidden rounded-full border"
                            style={{ backgroundColor: subtleSurface, borderColor: subtleBorder }}
                          >
                            {profile.top_games.map((game, i) => (
                              <div
                                key={game.game}
                                className="h-full border-r transition-all first:rounded-l-full last:rounded-r-full last:border-r-0"
                                style={{
                                  borderColor: subtleBorder,
                                  width: `${Math.max(6, (game.count / total) * 100)}%`,
                                  background: stops[i] || stops[2]
                                }}
                              />
                            ))}
                          </div>
                          <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1.5">
                            {profile.top_games.map((game, i) => (
                              <span key={game.game} className="inline-flex items-center gap-1.5 text-xs" style={{ color: primary }}>
                                <span
                                  className="w-2 h-2 rounded-full shrink-0"
                                  style={{
                                    background: stops[i] || stops[2],
                                    boxShadow: i === 0 ? `0 0 6px ${accent}66` : 'none'
                                  }}
                                />
                                <span className={i === 0 ? 'font-medium' : ''}>
                                  {getGameLabel(game.game, t)}
                                </span>
                              </span>
                            ))}
                          </div>
                        </>
                      )
                    })()}
                  </motion.div>
                )}

                {/* Активность и рефералы (partial+) — две stat-плитки */}
                {showPartial && (profile.activity_pct != null || profile.active_referrals != null) && (
                  <motion.div variants={SECTION_VARIANT} className="grid grid-cols-2 gap-3">
                    {profile.activity_pct != null && (
                      <div className={TILE_CLASS} style={{ backgroundColor: tileBg, borderColor: tileBorder }}>
                        <div className="flex items-center gap-2 mb-3">
                          <span
                            className="w-7 h-7 rounded-lg flex items-center justify-center shrink-0"
                            style={{ backgroundColor: `${accent}1A`, color: accent }}
                          >
                            <TrendingUp size={14} />
                          </span>
                          <span className="text-xs leading-tight" style={{ color: tilePrimary }}>
                            {t('publicProfile.activity', 'Активность')}
                          </span>
                        </div>
                        <div className="mb-2.5 text-xl font-bold tabular-nums" style={{ color: tilePrimary }}>
                          <AnimatedNumber value={profile.activity_pct} duration={0.8} />%
                        </div>
                        <div className="h-1.5 overflow-hidden rounded-full" style={{ backgroundColor: tileSurface }}>
                          <div
                            className="h-full rounded-full transition-all"
                            style={{
                              width: `${profile.activity_pct}%`,
                              backgroundColor: accent,
                              boxShadow: `0 0 8px ${accent}66`
                            }}
                          />
                        </div>
                      </div>
                    )}
                    {profile.active_referrals != null && (
                      <div className={`${TILE_CLASS} flex flex-col`} style={{ backgroundColor: tileBg, borderColor: tileBorder }}>
                        <div className="flex items-center gap-2 mb-3">
                          <span
                            className="w-7 h-7 rounded-lg flex items-center justify-center shrink-0"
                            style={{ backgroundColor: `${accent}1A`, color: accent }}
                          >
                            <Users size={14} />
                          </span>
                          <span className="text-xs leading-tight" style={{ color: tilePrimary }}>
                            {t('publicProfile.activeReferrals', 'Активные рефералы')}
                          </span>
                        </div>
                        <div className="mt-auto text-xl font-bold tabular-nums" style={{ color: tilePrimary }}>
                          <AnimatedNumber value={profile.active_referrals} duration={0.8} />
                        </div>
                      </div>
                    )}
                  </motion.div>
                )}

                {/* Топ скинов (partial+) — первые 4, «витрина для скриншотов»: */}
                {/* центрированный заголовок и логотип SKINOZ в подвале карточки для виральности */}
                {showPartial && profile.top_skins && (
                  <motion.div variants={SECTION_VARIANT} className={`${CARD_CLASS} p-5`} style={{ backgroundColor: contentBg, borderColor: subtleBorder }}>
                    <div className="mb-4 flex items-center justify-center gap-3">
                      <span className="h-px flex-1" style={{ backgroundImage: `linear-gradient(90deg, transparent, ${primary}26)` }} />
                      <span className="flex items-center gap-2 text-sm font-bold not-italic uppercase tracking-wide" style={{ color: primary }}>
                        <Package size={13} style={{ color: accent }} />
                        {t('publicProfile.topSkins', 'Топ моих скинов')}
                      </span>
                      <span className="h-px flex-1" style={{ backgroundImage: `linear-gradient(270deg, transparent, ${primary}26)` }} />
                    </div>
                    <ProfileSkinsGrid
                      items={profile.top_skins.slice(0, 4)}
                      colors={colors}
                      emptyText={t('publicProfile.noSkins', 'Пока нет скинов')}
                      onItemClick={openShowcase}
                    />
                    {/* Лого как на главной (LOGO_SRC + зелёное свечение) + адрес бота */}
                    <div className="mt-5 flex flex-col items-center border-t pt-4" style={{ borderColor: subtleBorder }}>
                      <div className="relative">
                        <img
                          src={LOGO_SRC}
                          alt="SKINOZ"
                          className="relative z-10 w-28"
                        />
                        <div className="pointer-events-none absolute left-1/2 top-1/2 h-6 w-full -translate-x-1/2 -translate-y-1/2 rounded-full bg-accent-primary/25 blur-xl" />
                      </div>
                      <span className="hidden mt-1.5 text-[10px] uppercase tracking-widest text-white/40">
                        t.me/{import.meta.env.VITE_TELEGRAM_BOT || 'skinozbot'}
                      </span>
                    </div>
                  </motion.div>
                )}

                {/* Инвентарь (public) — топ-4 самых дорогих предмета */}
                {showPublic && profile.inventory && (
                  <motion.div variants={SECTION_VARIANT} className={`${CARD_CLASS} p-5`} style={{ backgroundColor: contentBg, borderColor: subtleBorder }}>
                    <SectionTitle icon={ShoppingBag} color={accent} textColor={primary}>{t('publicProfile.inventory', 'Инвентарь')}</SectionTitle>
                    {profile.inventory.count != null && (
                      <div className="-mt-1.5 mb-3 text-sm flex items-center gap-1 tabular-nums" style={{ color: primary }}>
                        {formatPrice(profile.inventory.count)}{' '}
                        {t('publicProfile.itemsCount', { count: profile.inventory.count })}
                        <span className="flex items-center gap-0.5">
                          · <Gem size={12} style={{ color: accent }} />
                          {formatPrice(profile.inventory.total ?? 0)}
                        </span>
                      </div>
                    )}
                    {profile.inventory.items ? (
                      <ProfileSkinsGrid
                        items={[...profile.inventory.items]
                          .sort((a, b) => (Number(b.cost) || 0) - (Number(a.cost) || 0))
                          .slice(0, 4)}
                        colors={colors}
                        emptyText={t('publicProfile.inventoryEmpty', 'Инвентарь пуст')}
                      />
                    ) : null}
                  </motion.div>
                )}
              </>
            )}
          </div>
        </div>

        {/* Нижние действия — только на своём профиле: «Поделиться» (владелец в Telegram)
            и «Настройки» во всю ширину без первой */}
        {isOwner && (
          <motion.div variants={SECTION_VARIANT} className="mt-6 flex gap-3">
            {canShare && (
              <button
                onClick={handleShare}
                className="flex-1 p-4 rounded-xl bg-gradient-to-r from-accent-primary to-accent-secondary text-dark-primary font-bold flex items-center justify-center gap-2 transition-all active:scale-[0.98] shadow-[0_25px_50px_-12px_rgba(0,255,127,0.3)]"
              >
                <Share2 size={18} />
                {t('publicProfile.share', 'Поделиться')}
              </button>
            )}
            <button
              onClick={() => {
                playSound('select')
                navigate('/settings')
              }}
              className="flex-1 p-4 rounded-xl bg-surface border border-white/10 text-white font-semibold flex items-center justify-center gap-2 transition-all active:scale-[0.98] shadow-[inset_0_1px_0_rgba(255,255,255,0.07)]"
            >
              <Settings size={18} />
              {t('publicProfile.appSettings', 'Настройки')}
            </button>
          </motion.div>
        )}
        </motion.div>
      </div>

      <AchievementModal
        achievement={selectedAchievement}
        onClose={() => setSelectedAchievement(null)}
        language={language}
      />

      {/* Фуллскрин-витрина скина (золотистый брендовый стиль, без цветов профиля) */}
      <SkinShowcaseModal
        isOpen={Boolean(showcase)}
        onClose={() => setShowcase(null)}
        item={showcase?.item}
        rank={showcase?.rank}
        owner={`${profile.firstname || ''} ${profile.lastname || ''}`.trim() || undefined}
      />
    </div>
  )
}

export default PublicProfile
